import "./configPane.less";

import React from "react";
import { Navbar } from "react-bootstrap";
import { action, observable } from "mobx";
import { observer } from "mobx-react";
import EditIcon = require("react-icons/lib/md/mode-edit");
import CheckIcon = require("react-icons/lib/fa/check");
import DropdownArrowIcon = require("react-icons/lib/md/arrow-drop-down.js");
import DropupArrowIcon = require("react-icons/lib/md/arrow-drop-up.js");
import { MdIconButton } from "../buttons/icons";
import { ZzFlatButton } from "../buttons/buttons";
import FlatButton from "material-ui/FlatButton";
import {
  ChartConfigDto,
  ChartType,
  DataSeries,
  PaneChartConfig,
} from "./chartDomain";
import { LovItem } from "core/metamodel/mdEntity";
import {
  Dialog,
  MenuItem,
  RadioButton,
  RadioButtonGroup,
  SelectField,
  TextField,
  TouchTapEvent,
  Checkbox,
} from "material-ui";
import Axios, { AxiosResponse } from "axios";
import { loc } from "core/common/config/locales";
import Grid from "core/grid/domain/grid";
import { CONFIGLEVEL } from "core/metamodel/metaConstant";

interface ConfigPaneProps {
  initChartConfigs: Map<number, PaneChartConfig>;
  currentChartConfigs: Map<number, PaneChartConfig>;
  activeChartConfigId: number;
  onChartConfigChange: (
    chartConfig: PaneChartConfig,
    changeType?: ConfigChangeTypeEnum
  ) => void;
  onOtherColumnSelected: (columnName: string, columnId: string) => void;
  onActiveChartConfigChange: (chartId: number, reinitCurrent: boolean) => void;
  onChartConfigSave: (chartConfig: ChartConfigDto) => void;
  onNewChart: () => void;
  onDeleteChart: () => void;

  currentDataSeries: DataSeries[];
  initDataSeries: DataSeries[];

  allSelected: boolean;
  onSelectAll: (isAllSelected: boolean) => void;

  entityName: string;
  grid: Grid;
}

@observer
export class ConfigPane extends React.Component<ConfigPaneProps> {
  @observable private anyChange: boolean = false;
  @observable private editChartName: boolean = false;

  //used to show unsaved changes dialog, number is used to change active config and cleared
  @observable private switchConfigId: number = undefined;
  @observable private showOthersColumns: boolean = false;

  @observable private columnSearch: string = "";

  legendItemIsChecked = (name: string): boolean => {
    let ds: DataSeries = this.props.currentDataSeries.find(
      (s) => s.name == name
    );
    if (ds != null) {
      return ds.enable;
    }
    return false;
  };

  ////////// ENABLE AND DISABLE DATA SERIES //////////
  @action
  legendItemClicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    let ds: DataSeries = this.props.currentDataSeries.find(
      //TODO: put outside component
      (s) => s.name == e.target.value
    );
    if (ds != null) {
      ds.enable = !ds.enable;

      if (ds.enable && !ds.isUsedOnGrid) {
        this.props.onOtherColumnSelected(ds.name, ds.id);
      }

      const activeConfig = this.props.currentChartConfigs.get(
        this.props.activeChartConfigId
      );
      let changedConfig = Object.assign({}, activeConfig);
      if (ds.enable) {
        changedConfig.chartColumnsIds.push(ds.id);
      } else {
        const foundIndex = changedConfig.chartColumnsIds.findIndex(
          (columnId) => columnId == ds.id
        );
        if (foundIndex != -1) {
          changedConfig.chartColumnsIds.splice(foundIndex);
        }
      }
      this.props.onChartConfigChange(changedConfig);
    }

    this.checkChanges();
  };

  renderControlElements = () => {
    let legendList = []; //active columns on grid
    let activeLegendList = [];
    let othersLegendList = []; //inactive columns on grid
    let activeOthersLegendList = [];

    this.props.currentDataSeries.forEach((ds) => {
      if (this.columnSearch) {
        if (ds.name.toLowerCase().indexOf(this.columnSearch) === -1) return;
      }

      const legendItem = (
        <div id={ds.name} className="legend-list-item" key={"item-" + ds.name}>
          <input
            value={ds.name}
            type="checkbox"
            checked={this.legendItemIsChecked(ds.name)}
            onChange={this.legendItemClicked}
          />
          <label style={{ marginRight: 5 }}>{ds.name}</label>
        </div>
      );

      if (ds.isUsedOnGrid) {
        ds.enable
          ? activeLegendList.push(legendItem)
          : legendList.push(legendItem);
      } else {
        ds.enable
          ? activeOthersLegendList.push(legendItem)
          : othersLegendList.push(legendItem);
      }
    });

    const showOthersList: boolean =
      activeOthersLegendList.length > 0 || othersLegendList.length > 0;
    return (
      <div className="legend">
        <div className="legend-column">
          <h4>{loc.i18n`chart.config.gridColumns`}</h4>
          <div>{activeLegendList}</div>
          {activeLegendList.length > 0 && <div className="separator"></div>}
          <div className="legend-list">{legendList}</div>
        </div>
        {showOthersList && (
          <div className="legend-column">
            <div
              className="others-header"
              onClick={() => (this.showOthersColumns = !this.showOthersColumns)}
            >
              <h4>{loc.i18n`chart.config.inactiveGridColumns`}</h4>
              {this.showOthersColumns ? (
                <DropupArrowIcon size={30} />
              ) : (
                <DropdownArrowIcon size={30} />
              )}
            </div>

            <div>{activeOthersLegendList}</div>
            {activeOthersLegendList.length > 0 && this.showOthersColumns && (
              <div className="separator"></div>
            )}
            {this.showOthersColumns && (
              <div className="legend-list">{othersLegendList}</div>
            )}
          </div>
        )}
      </div>
    );
  };

  renderChartConfigSelectItems() {
    let result = [];
    let values = this.props.currentChartConfigs.values();
    while (true) {
      let iteratorResult: IteratorResult<PaneChartConfig> = values.next();
      if (iteratorResult.done) break;
      let value = iteratorResult.value;
      result.push(
        <MenuItem
          value={value.chartId}
          key={`select-chart-config-${value.chartId}`}
          primaryText={value.chartName}
        />
      );
    }
    return result;
  }

  checkChanges() {
    const p = this.props;
    const initConfig = p.initChartConfigs.get(p.activeChartConfigId);
    const currentConfig = p.currentChartConfigs.get(p.activeChartConfigId);

    let dataSeriesChanged: boolean = false;
    p.initDataSeries.forEach((initDataSeries: DataSeries, i: number) => {
      if (initDataSeries.enable !== p.currentDataSeries[i].enable) {
        dataSeriesChanged = true;
        return;
      }
    });

    if (dataSeriesChanged) console.log("CHECK_CHANGES: Data series changed");

    this.anyChange =
      dataSeriesChanged ||
      initConfig.chartName !== currentConfig.chartName ||
      initConfig.chartType !== currentConfig.chartType ||
      initConfig.chartXField !== currentConfig.chartXField ||
      initConfig.configLevel !== currentConfig.configLevel ||
      initConfig.chartSorted !== currentConfig.chartSorted;
  }

  changeActiveConfig(configId: number, reinitCurrent: boolean) {
    this.anyChange = false;
    this.props.onActiveChartConfigChange(configId, reinitCurrent);
  }

  render() {
    const p = this.props;
    const activeConfig = this.props.currentChartConfigs.get(
      this.props.activeChartConfigId
    );
    return p.currentChartConfigs ? (
      <div className="config-pane">
        <Dialog
          actions={[
            <FlatButton
              label={loc.i18n`No, return back`}
              primary
              onClick={() => (this.switchConfigId = undefined)}
            />,
            <FlatButton
              label={loc.i18n`Yes, continue`}
              secondary
              onClick={() => {
                this.changeActiveConfig(this.switchConfigId, true);
                this.switchConfigId = undefined;
              }}
            />,
          ]}
          open={!!this.switchConfigId}
          title={loc.i18n`chart.sureAnotherGraphSelect`}
        />
        <Navbar className="graph-nav-bar" fluid>
          <div className="nav-items-container">
            {this.editChartName ? (
              <TextField
                id="tf-chart-name"
                value={activeConfig.chartName}
                autoFocus={true}
                onFocus={(e) => {
                  e.target.select();
                }}
                onChange={(e) => {
                  let changedConfig = Object.assign({}, activeConfig);
                  changedConfig.chartName = e.target.value;
                  p.onChartConfigChange(changedConfig);
                }}
              />
            ) : (
              <SelectField
                value={this.props.activeChartConfigId}
                onChange={(e: TouchTapEvent, i: number, value: number) => {
                  if (value === this.props.activeChartConfigId) return;
                  if (this.anyChange) {
                    this.switchConfigId = value;
                  } else {
                    this.changeActiveConfig(value, false);
                  }
                }}
              >
                {this.renderChartConfigSelectItems()}
              </SelectField>
            )}
            <MdIconButton
              style={{ alignSelf: "center", color: "pink" }}
              onClick={() => {
                this.editChartName = !this.editChartName;
                if (!this.editChartName) this.checkChanges();
              }}
            >
              {this.editChartName ? (
                <CheckIcon size={24} />
              ) : (
                <EditIcon size={24} />
              )}
            </MdIconButton>
          </div>
          <div className="nav-items-container">
            <ZzFlatButton
              onClick={() => p.onNewChart()}
              label={loc.i18n`chart.config.new`}
              disabled={activeConfig.isNew}
            />
            <ZzFlatButton
              onClick={() => {
                const p = this.props;

                let chartColumnsIds: string[] = [];
                p.currentDataSeries.forEach((dS: DataSeries, i: number) => {
                  if (dS.enable) chartColumnsIds.push(dS.id);
                });

                Axios.post(
                  `/api/core/grid/${p.grid.activeGridConfig.gridId}/chart/config`,
                  {
                    chartId: activeConfig.isNew
                      ? undefined
                      : activeConfig.chartId,
                    chartName: activeConfig.chartName,
                    chartType: activeConfig.chartType,
                    chartColumnsIds: chartColumnsIds.join(";"),
                    chartXField: activeConfig.chartXField,
                    configLevel: activeConfig.configLevel,
                    entityName: p.entityName,
                    chartSorted: activeConfig.chartSorted,
                  }
                ).then((response: AxiosResponse) => {
                  this.props.onChartConfigSave(response.data);
                  this.checkChanges();
                });
              }}
              label={loc.i18n`chart.config.save`}
              disabled={
                this.editChartName || (!activeConfig.isNew && !this.anyChange)
              }
            />
            <ZzFlatButton
              onClick={() => {
                Axios.delete(
                  `/api/core/grid/chart/${activeConfig.chartId}`
                ).then(() => {
                  this.props.onDeleteChart();
                });
              }}
              label={loc.i18n`chart.config.delete`}
              disabled={activeConfig.isNew}
            />
          </div>
        </Navbar>
        <RadioButtonGroup
          name="Config level radio group"
          valueSelected={activeConfig.configLevel}
          onChange={(_, value: CONFIGLEVEL) => {
            let changedConfig = Object.assign({}, activeConfig);
            changedConfig.configLevel = value;
            p.onChartConfigChange(changedConfig);
            this.checkChanges();
          }}
        >
          {[CONFIGLEVEL.USER, CONFIGLEVEL.TENANT].map((value) => (
            <RadioButton
              style={{ width: "auto" }}
              key={value}
              value={value}
              label={value}
            />
          ))}
        </RadioButtonGroup>
        <div>{loc.i18n`chart.config.chartType`}</div>
        <SelectField
          value={activeConfig.chartType}
          onChange={(_: TouchTapEvent, __: number, value: ChartType) => {
            let changedConfig = Object.assign({}, activeConfig);
            changedConfig.chartType = value;
            p.onChartConfigChange(changedConfig);
            this.checkChanges();
          }}
        >
          {chartTypes.map((chartType) => (
            <MenuItem
              value={chartType.value}
              key={chartType.value}
              primaryText={loc.get(chartType.label)}
            />
          ))}
        </SelectField>
        <div>{loc.i18n`chart.config.xAxis`}</div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <SelectField
            value={activeConfig.chartXField || ""}
            onChange={(_: TouchTapEvent, __: number, value: string) => {
              let changedConfig = Object.assign({}, activeConfig);
              changedConfig.chartXField = value;
              p.onChartConfigChange(changedConfig, ConfigChangeTypeEnum.X_AXIS);
              this.checkChanges();
            }}
          >
            <MenuItem
              value={""}
              key={"None"}
              primaryText={loc.i18n`chart.config.noXFieldSelected`}
            />
            {p.initDataSeries.map((dS: DataSeries) => (
              <MenuItem value={dS.id} key={dS.id} primaryText={dS.name} />
            ))}
          </SelectField>
          <Checkbox
            style={{ marginLeft: 10 }}
            label="Sorted"
            checked={activeConfig.chartSorted}
            disabled={!activeConfig.chartXField}
            onCheck={() => {
              let changedConfig = Object.assign({}, activeConfig);
              changedConfig.chartSorted = !changedConfig.chartSorted;
              p.onChartConfigChange(changedConfig, ConfigChangeTypeEnum.SORTED);
              this.checkChanges();
            }}
          />
        </div>

        <div>{loc.i18n`chart.config.searchColumns`}</div>
        <TextField
          id="tf-search"
          value={this.columnSearch}
          onChange={(_, value: string) => {
            this.columnSearch = value;
          }}
        />
        {this.renderControlElements()}
      </div>
    ) : (
      <div>loading</div>
    );
  }
}

const chartTypes: LovItem<ChartType>[] = [
  { value: ChartType.BAR, label: `chart.label.bar` },
  { value: ChartType.LINE, label: `chart.label.line` },
  { value: ChartType.AREA, label: `chart.label.area` },
];

export enum ConfigChangeTypeEnum {
  X_AXIS,
  SORTED,
}



// WEBPACK FOOTER //
// ./src/core/components/chart/configPane.tsx